import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ShieldCheck, Eye, EyeOff, ExternalLink, X } from "lucide-react";
import { toast } from "sonner";

/**
 * AdminPreviewBadge
 *
 * Only visible when:
 *   1) the current user is `role === "admin"`, AND
 *   2) Coming Soon Mode is currently ON (so the admin is on the *private*
 *      version of the site while the public sees the waitlist page).
 *
 * The badge signals "you are viewing the full app while the world sees the
 * Coming Soon page" and offers 1-click controls to:
 *   - Preview the public Coming Soon page (opens in new tab)
 *   - Jump straight to the Launch Control Center
 *   - Instantly disable Coming Soon Mode (with confirmation)
 *
 * Never rendered for non-admins.
 */
export default function AdminPreviewBadge() {
  const { user } = useAuth();
  const [enabled, setEnabled] = useState(false);
  const [checked, setChecked] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/api/launch/config");
      setEnabled(!!data?.coming_soon_enabled);
    } catch (e) {
      setEnabled(false);
    } finally {
      setChecked(true);
    }
  }, []);

  useEffect(() => {
    if (user?.role !== "admin") { setChecked(true); return; }
    load();
    const id = setInterval(load, 30_000);
    return () => clearInterval(id);
  }, [user?.role, load]);

  const disable = async () => {
    if (!window.confirm("Turn Coming Soon Mode OFF? Every visitor will immediately gain access to the live FlowFinder app.")) return;
    setBusy(true);
    try {
      await api.put("/api/admin/launch/config", { coming_soon_enabled: false });
      toast.success("Coming Soon Mode is OFF — FlowFinder is now public.");
      setEnabled(false);
    } catch (e) {
      toast.error("Couldn't disable Coming Soon Mode. Please try again from Launch Control.");
    } finally {
      setBusy(false);
    }
  };

  // Bail out early — nothing to render for non-admins or when mode is OFF.
  if (!checked) return null;
  if (user?.role !== "admin") return null;
  if (!enabled) return null;

  // Collapsed pill (small, unobtrusive)
  if (collapsed) {
    return (
      <button
        type="button"
        data-testid="admin-preview-badge-collapsed"
        onClick={() => setCollapsed(false)}
        className="fixed bottom-5 left-5 z-[100] inline-flex items-center gap-2 px-3 py-2 rounded-full text-[11.5px] font-mono uppercase tracking-[0.15em] text-black bg-[#00ff9c] shadow-lg shadow-[#00ff9c]/20 hover:bg-[#00e089] transition-all"
        aria-label="Expand admin preview controls"
      >
        <ShieldCheck className="w-3.5 h-3.5" strokeWidth={2.4} />
        <span>Admin Preview</span>
      </button>
    );
  }

  // Full panel
  return (
    <div
      data-testid="admin-preview-badge"
      className="fixed bottom-5 left-5 z-[100] max-w-[300px] rounded-2xl border border-[#00ff9c]/40 bg-[#00110a]/95 shadow-2xl shadow-[#00ff9c]/10 overflow-hidden"
      style={{ backdropFilter: "blur(16px)" }}
    >
      <div className="flex items-start justify-between gap-3 px-4 py-3 border-b border-[#00ff9c]/15">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-lg bg-[#00ff9c] flex items-center justify-center shrink-0">
            <ShieldCheck className="w-4 h-4 text-black" strokeWidth={2.4} />
          </div>
          <div>
            <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-[#00ff9c]">Admin Preview</div>
            <div className="text-white text-[13px] font-semibold tracking-tight leading-tight mt-0.5">
              Coming Soon Mode is ON
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setCollapsed(true)}
          className="text-white/40 hover:text-white transition p-0.5 -m-0.5"
          aria-label="Collapse admin preview panel"
          data-testid="admin-preview-collapse"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="px-4 pt-2.5 pb-3">
        <p className="text-[12px] text-white/60 leading-relaxed">
          You&apos;re viewing the full app. Every non-admin visitor sees the waitlist page.
        </p>
        <div className="mt-3 flex flex-col gap-1.5">
          <Link
            to="/admin/launch"
            data-testid="admin-preview-launch-control"
            className="inline-flex items-center justify-between gap-2 text-[12.5px] text-white bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-white/25 rounded-lg px-3 py-2 transition"
          >
            <span>Launch Control Center</span>
            <ExternalLink className="w-3.5 h-3.5 text-white/50" />
          </Link>
          <a
            href="/?preview=coming-soon"
            target="_blank"
            rel="noreferrer"
            data-testid="admin-preview-see-public"
            className="inline-flex items-center justify-between gap-2 text-[12.5px] text-white bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-white/25 rounded-lg px-3 py-2 transition"
          >
            <span className="inline-flex items-center gap-2">
              <Eye className="w-3.5 h-3.5" /> See what visitors see
            </span>
            <ExternalLink className="w-3.5 h-3.5 text-white/50" />
          </a>
          <button
            type="button"
            data-testid="admin-preview-disable-mode"
            onClick={disable}
            disabled={busy}
            className="inline-flex items-center justify-center gap-2 text-[12.5px] font-semibold text-black bg-[#00ff9c] hover:bg-[#00e089] rounded-lg px-3 py-2 transition disabled:opacity-60"
          >
            <EyeOff className="w-3.5 h-3.5" strokeWidth={2.4} />
            {busy ? "Turning off…" : "Go public — disable waitlist mode"}
          </button>
        </div>
      </div>
    </div>
  );
}
